import {
  AwayTeamComponent,
  HomeTeamComponent,
  IconsResourceComponent,
  StatusMatchComponent,
  VsScoresComponent,
} from '@/app/components/shared/match-by-competition';
import { CalendarService } from '@/app/services/calendar.service';
import { TranslationService } from '@/app/services/translation.service';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { environment } from '@environments/environment';
import { EStatusMatches, MatchApi } from '../../../home/models';
import { TeamMatchesActiveService } from '../../services';
import { ELanguage } from '@/app/utils';
import { ROUTE_FOOTBALL_MATCHS, ROUTE_FOOTBALL_MATCHS_EN } from '@/app/routes';

@Component({
  selector: 'app-match-by-team-item',
  standalone: true,
  imports: [
    RouterLink,
    HomeTeamComponent,
    VsScoresComponent,
    AwayTeamComponent,
    StatusMatchComponent,
    IconsResourceComponent,
  ],
  templateUrl: './match-by-team-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatchByTeamItemComponent {
  // ✅ Servicios
  private translationService = inject(TranslationService);
  private calendarService = inject(CalendarService);
  private teamMatchesActiveService = inject(TeamMatchesActiveService);

  // ✅ Constantes
  readonly urlIcon = environment.urlIcon;
  readonly imageDefault = `${environment.urlIMG}default.png`;
  noPro = environment.envName === 'local' || environment.envName === 'dev';

  data = input.required<MatchApi>();
  // wsMatchesData = input<Record<string, IWsMatch>>({});

  readonly selectedDate = computed(() =>
    this.calendarService.selectedDate().replaceAll('-', '')
  );

  // ✅ Señal para guardar último ws válido por partido
  readonly liveDataMap = signal<Record<string, Partial<MatchApi>>>({});

  // ✅ Efecto para actualizar liveDataMap si llega ws
  private syncLiveData = effect(
    () => {
      const id = this.data().Id;
      const ws = this.teamMatchesActiveService.wsMatchesData()[id];

      if (ws) {
        this.liveDataMap.update((prev) => ({
          ...prev,
          [id]: {
            AwayHalfScore: ws.halfAwayScore,
            AwayScore: ws.awayScore,
            GamePeriod: ws.gamePeriod,
            Status: ws.state,
            HomeHalfScore: ws.halfHomeScore,
            HomeScore: ws.homeScore,
            GameTimeAdd: ws.gameTimeAdd,
            AwayPenaltyScore: ws.penaltyAwayScore,
            HomePenaltyScore: ws.penaltyHomeScore,
            PeriodStartTime:
              ws.periodStartTime !== null
                ? ws.periodStartTime.toISOString()
                : this.data().PeriodStartTime,
          },
        }));

        // this.hasWs.set(true);
      }
    },
    { allowSignalWrites: true }
  );

  liveMatch = computed(() => {
    const base = this.data();
    const liveMatch = this.liveDataMap()[base.Id];

    return {
      ...base,
      ...liveMatch,
    };
  });

  getUrlForMatches(): string {
    const slug = this.translationService.slugifyNameRoute(
      {
        NameTranslation: this.liveMatch().HomeNameTranslation,
        defaultName: this.liveMatch().HomeName,
      },
      {
        NameTranslation: this.liveMatch().AwayNameTranslation,
        defaultName: this.liveMatch().AwayName,
      }
    );

    if (this.translationService.getLangLocalStorage() === ELanguage.EN) {
      return `/${ROUTE_FOOTBALL_MATCHS_EN.replace(':slug', slug).replace(
        ':matchId',
        this.liveMatch().Id
      )}`;
    } else {
      return `/${ROUTE_FOOTBALL_MATCHS.replace(':slug', slug).replace(
        ':matchId',
        this.liveMatch().Id
      )}`;
    }
  }

  getUrlMatch(): string {
    const lang = this.translationService.getLangLocalStorage();
    const sport = lang === 'es' ? 'futbol' : 'football';
    const match = lang === 'es' ? 'partido' : 'match';

    const slug = this.translationService.slugifyNameRoute(
      {
        NameTranslation: this.liveMatch().HomeNameTranslation,
        defaultName: this.liveMatch().HomeName,
      },
      {
        NameTranslation: this.liveMatch().AwayNameTranslation,
        defaultName: this.liveMatch().AwayName,
      }
    );

    return `${sport}/${match}/${slug}/${this.liveMatch().Id}`;
  }

  teamCountryName() {
    return '';
  }

  homeTeamData() {
    const data = this.liveMatch();
    return {
      id: data.HomeId,
      name: data.HomeName,
      logo: data.HomeLogo || this.imageDefault,
      score: data.HomeScore,
      halfScore: data.HomeHalfScore,
      penaltyScore: data.HomePenaltyScore,
      nameTranslation: data.HomeNameTranslation,
      tsExternalId: data.HomeTsExternalId,
    };
  }

  awayTeamData() {
    const data = this.liveMatch();
    return {
      id: data.AwayId,
      name: data.AwayName,
      logo: data.AwayLogo || this.imageDefault,
      score: data.AwayScore,
      halfScore: data.AwayHalfScore,
      penaltyScore: data.AwayPenaltyScore,
      nameTranslation: data.AwayNameTranslation,
      tsExternalId: data.AwayTsExternalId,
    };
  }

  winHomeTeam(): boolean {
    const m = this.liveMatch();
    return m.Status === EStatusMatches.ENDED && m.HomeScore > m.AwayScore;
  }

  winAwayTeam(): boolean {
    const m = this.liveMatch();
    return m.Status === EStatusMatches.ENDED && m.AwayScore > m.HomeScore;
  }
}
